import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Buildings, ChatCircleDots, CurrencyDollar, ChartBar, Lightning,
  Sparkle, TrendUp, TrendDown, Coins, Receipt, ArrowRight,
} from "@phosphor-icons/react";

/* ─────────── format helpers ─────────── */
const fmtBRL = (cents) =>
  ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
const fmtBRLn = (brl) =>
  Number(brl || 0).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
const fmtInt = (n) => (n || 0).toLocaleString("pt-BR");
const fmtTokens = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} mi`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} k`;
  return v.toString();
};

/* ─────────── components ─────────── */

function HeroKpi({ label, value, hint, accent, trend, testId }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6 min-w-0 hover:bg-white/[0.05] transition-colors" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">{label}</div>
      <div className={`font-heading text-[34px] leading-none font-semibold tracking-tight mt-3 tabular-nums ${accent || "text-white"}`}>{value}</div>
      <div className="flex items-center gap-2 mt-2.5 text-[11.5px]">
        {trend && (
          <span className={`inline-flex items-center gap-0.5 ${trend.up ? "text-emerald-400" : "text-rose-400"}`}>
            {trend.up ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
            {trend.label}
          </span>
        )}
        {hint && <span className="text-slate-500">{hint}</span>}
      </div>
    </div>
  );
}

function MiniKpi({ label, value, hint, icon: Icon, accent, testId }) {
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</div>
        {Icon && <Icon size={12} weight="regular" className={accent || "text-slate-500"} />}
      </div>
      <div className="font-heading text-xl font-semibold text-white mt-2 tabular-nums tracking-tight">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

/* ─────────── page ─────────── */

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [topMargin, setTopMargin] = useState(null);   // worst-margin tenants
  const [topCost, setTopCost] = useState(null);       // heaviest cost tenants
  const [repsKpi, setRepsKpi] = useState(null);       // total / active rep counts
  const [days, setDays] = useState(30);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/admin/dashboard?days=${days}`).then(({ data }) => setData(data));
    api.get(`/admin/tenants?days=${days}&sort=margin`).then(({ data }) =>
      setTopMargin((data.items || []).filter((t) => t.mrr_cents > 0).slice(0, 5)),
    );
    api.get(`/admin/tenants?days=${days}&sort=cost`).then(({ data }) =>
      setTopCost((data.items || []).filter((t) => (t.cost_brl || 0) > 0).slice(0, 5)),
    );
  }, [days]);

  // Reps KPI doesn't depend on the period selector — fetch once.
  useEffect(() => {
    api.get("/admin/representatives").then(({ data: rows }) => {
      const list = rows || [];
      setRepsKpi({
        total: list.length,
        active: list.filter((r) => r.status === "active").length,
        with_clients: list.filter((r) => (r.total_clients || 0) > 0).length,
      });
    }).catch(() => setRepsKpi({ total: 0, active: 0, with_clients: 0 }));
  }, []);

  const t = data?.tenants || {};
  const m = data?.messages || {};
  const ai = data?.ai || {};
  const ops = data?.operations || {};
  const fin = data?.financial || {};

  const marginBrl = fin.estimated_margin_brl ?? 0;
  const marginPct = fin.estimated_margin_pct ?? 0;
  const marginGreen = marginBrl >= 0;

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-7 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Painel financeiro</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">Visão do gestor</h1>
          <p className="text-sm text-slate-400 mt-1.5">Operação consolidada · últimos {days} dias</p>
        </div>
        <div className="flex items-center gap-1 text-[12px]" data-testid="master-period-selector">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`days-${d}`}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                days === d ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </header>

      {/* ─── Hero KPIs — financeiro primeiro, gigante ───────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <HeroKpi
          label="Faturamento total"
          value={fmtBRLn(fin.total_revenue_brl)}
          hint={`base ${fmtBRLn(fin.mrr_brl)}${fin.pack_revenue_brl > 0 ? ` + pacotes ${fmtBRLn(fin.pack_revenue_brl)}` : ""}`}
          accent="text-emerald-400"
          testId="kpi-revenue"
        />
        <HeroKpi
          label="Custo bruto IA"
          value={fmtBRLn(fin.estimated_cost_brl)}
          hint={`${fmtTokens(ai.tokens_total_estimated)} tokens consumidos`}
          accent={(fin.estimated_cost_brl || 0) > (fin.total_revenue_brl || 0) ? "text-rose-400" : "text-amber-400"}
          testId="kpi-cost"
        />
        <HeroKpi
          label="Margem"
          value={fmtBRLn(marginBrl)}
          hint={fin.total_revenue_brl ? `${marginPct.toFixed(1)}% da receita` : "Sem receita no período"}
          accent={marginGreen ? "text-emerald-400" : "text-rose-400"}
          testId="kpi-margin"
        />
        <HeroKpi
          label="Clientes ativos"
          value={fmtInt(t.active_14d)}
          hint={`${t.paying || 0} pagantes · ARPU ${fmtBRLn(fin.arpu_brl)}`}
          accent="text-white"
          testId="kpi-active"
        />
      </section>

      {/* ─── Secondary KPI strip ───────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
        <MiniKpi
          label="Tokens (in)"
          value={fmtTokens(ai.tokens_in_estimated)}
          hint="estimativa baseada em chamadas"
          icon={Coins} accent="text-blue-400"
          testId="mini-tokens-in"
        />
        <MiniKpi
          label="Tokens (out)"
          value={fmtTokens(ai.tokens_out_estimated)}
          hint="estimativa baseada em chamadas"
          icon={Coins} accent="text-indigo-400"
          testId="mini-tokens-out"
        />
        <MiniKpi
          label="Mensagens"
          value={fmtInt(m.total)}
          hint={`${fmtInt(m.sent)} env · ${fmtInt(m.received)} rec`}
          icon={ChatCircleDots} accent="text-slate-400"
          testId="mini-messages"
        />
        <MiniKpi
          label="Receita gerada (clientes)"
          value={fmtBRL(ops.ai_attributed_revenue_cents)}
          hint="atribuída à IA"
          icon={Receipt} accent="text-emerald-400"
          testId="mini-revenue-clients"
        />
        <button
          type="button"
          onClick={() => navigate("/admin-master/representantes")}
          data-testid="mini-reps"
          className="bg-white/[0.02] border border-white/10 rounded-lg p-4 text-left hover:bg-white/[0.05] hover:border-white/20 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">
              Representantes
            </div>
            <Lightning size={12} weight="regular" className="text-amber-400" />
          </div>
          <div className="font-heading text-2xl font-semibold tabular-nums mt-2 text-white">
            {repsKpi ? fmtInt(repsKpi.total) : "—"}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {repsKpi
              ? `${fmtInt(repsKpi.active)} ativos · ${fmtInt(repsKpi.with_clients)} com clientes`
              : "carregando…"}
          </div>
        </button>
      </section>

      {/* ─── Two-column: cost leaders + margin losers ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <TenantRanking
          title="Maior custo IA"
          subtitle="Top 5 clientes que mais consumiram tokens"
          icon={Sparkle}
          accent="text-amber-400"
          items={topCost}
          metric={(it) => fmtBRLn(it.cost_brl)}
          subMetric={(it) => `${fmtTokens(it.tokens_total)} tok · ${fmtInt(it.ai_calls)} chamadas`}
          onClick={(it) => navigate(`/admin-master/tenants/${it.id}`)}
        />
        <TenantRanking
          title="Margem mais baixa"
          subtitle="Clientes pagantes em risco financeiro"
          icon={ChartBar}
          accent="text-rose-400"
          items={topMargin}
          metric={(it) => fmtBRLn(it.margin_brl)}
          subMetric={(it) => `MRR ${fmtBRLn(it.mrr_brl)} − custo ${fmtBRLn(it.cost_brl)}`}
          highlight={(it) => (it.margin_brl ?? 0) < 0}
          onClick={(it) => navigate(`/admin-master/tenants/${it.id}`)}
        />
      </div>

      {/* ─── Status distribution ───────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm font-semibold text-white tracking-tight">Carteira de clientes</h3>
          <button
            onClick={() => navigate("/admin-master/tenants")}
            data-testid="dashboard-see-all-tenants"
            className="text-[12px] text-slate-400 hover:text-white inline-flex items-center gap-1 transition-colors"
          >
            Ver todos os clientes <ArrowRight size={11} weight="bold" />
          </button>
        </div>
        <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Total</div>
              <div className="font-heading text-2xl font-semibold text-white mt-1.5 tabular-nums">{fmtInt(t.total)}</div>
            </div>
            {Object.entries(t.by_status || {}).map(([status, count]) => (
              <div key={status}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">{status}</div>
                <div className={`font-heading text-2xl font-semibold mt-1.5 tabular-nums ${
                  status === "active" ? "text-emerald-400"
                  : status === "suspended" ? "text-rose-400"
                  : status === "cancelled" ? "text-slate-500"
                  : "text-amber-400"
                }`}>{count}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─────────── tenant ranking widget ─────────── */

function TenantRanking({ title, subtitle, icon: Icon, accent, items, metric, subMetric, highlight, onClick }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5">
      <header className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon size={13} weight="regular" className={accent} />}
            <h3 className="font-heading text-[13px] font-semibold text-white tracking-tight">{title}</h3>
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</div>
        </div>
      </header>

      {items === null && (
        <div className="text-[12px] text-slate-500 py-3">Carregando…</div>
      )}
      {items?.length === 0 && (
        <div className="text-[12px] text-slate-500 py-3">Nenhum dado no período.</div>
      )}

      <div className="space-y-0">
        {items?.map((it, i) => {
          const danger = highlight?.(it);
          return (
            <button
              key={it.id}
              onClick={() => onClick(it)}
              data-testid={`ranking-row-${it.id}`}
              className={`w-full text-left flex items-center gap-3 py-2.5 border-t border-white/5 first:border-t-0 hover:bg-white/[0.02] -mx-2 px-2 rounded transition-colors ${
                danger ? "bg-rose-500/[0.05]" : ""
              }`}
            >
              <div className="w-6 h-6 rounded-md bg-white/5 text-slate-400 text-[11px] font-semibold flex items-center justify-center tabular-nums">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-white font-medium truncate">{it.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{subMetric(it)}</div>
              </div>
              <div className={`text-[14px] font-semibold tabular-nums ${danger ? "text-rose-400" : "text-white"}`}>
                {metric(it)}
              </div>
              <ArrowRight size={11} weight="bold" className="text-slate-600" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
